import React, { useEffect, useState, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Swal from "sweetalert2";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useIsAdmin } from "../../hooks/useIsAdmin";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const STATUS_VARIANT = { pending: "warning", confirmed: "success", rejected: "secondary" };

const AdminPayments = () => {
  const { t } = useTranslations();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [actingId, setActingId] = useState(null);

  const authHeaders = useCallback(async () => {
    // force refresh: usa el token más reciente (refleja revocación de rol al instante).
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setDenied(false);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/payments?status=${status}`, { headers });
      if (res.status === 401) {
        setDenied(true);
        setItems([]);
        return;
      }
      const data = await res.json();
      setItems(data.payments || []);
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
    } finally {
      setLoading(false);
    }
  }, [status, authHeaders]);

  useEffect(() => {
    // Solo carga si el rol admin está confirmado en el cliente.
    if (!adminLoading && isAdmin) load();
  }, [load, adminLoading, isAdmin]);

  const act = async (paymentId, action) => {
    let reason;
    if (action === "reject") {
      const r = await Swal.fire({
        title: t("rejectReasonPrompt"),
        input: "text",
        showCancelButton: true,
      });
      if (!r.isConfirmed) return;
      reason = r.value || "";
    }
    try {
      setActingId(paymentId);
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/payments`, {
        method: "POST",
        headers,
        body: JSON.stringify({ paymentId, action, reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({
        icon: "success",
        title: action === "confirm" ? t("paymentConfirmed") : t("paymentRejected"),
        timer: 2500,
        showConfirmButton: false,
      });
      setItems((prev) => prev.filter((p) => p.id !== paymentId));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto" style={{ maxWidth: 960 }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <h2 className="mb-0">{t("adminPaymentsTitle")}</h2>
            {isAdmin && (
              <Form.Select
                style={{ maxWidth: 220 }}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="pending">{t("statusPending")}</option>
                <option value="confirmed">{t("statusConfirmed")}</option>
                <option value="rejected">{t("statusRejected")}</option>
              </Form.Select>
            )}
          </div>

          {adminLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : !isAdmin ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("notAuthorized")}</Card.Body>
            </Card>
          ) : loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : denied ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("notAuthorized")}</Card.Body>
            </Card>
          ) : items.length === 0 ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("noPayments")}</Card.Body>
            </Card>
          ) : (
            items.map((p) => (
              <Card key={p.id} className="mb-3 border-0 shadow-sm" style={{ borderRadius: 14 }}>
                <Card.Body>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-semibold">{p.email || p.uid}</div>
                      <div className="small text-muted">
                        {p.method} · {p.plan === "annual" ? t("planAnnual") : t("planMonthly")}
                        {p.amount ? ` · ${p.amount}` : ""}
                      </div>
                      <div className="small mt-1">
                        <span className="text-muted">{t("paymentReference")}: </span>
                        <span className="fw-semibold">{p.reference || "—"}</span>
                        {p.bank ? ` · ${p.bank}` : ""}
                        {p.paidAt ? ` · ${p.paidAt}` : ""}
                      </div>
                    </div>
                    <Badge bg={STATUS_VARIANT[p.status] || "light"}>{p.status}</Badge>
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    {p.proofUrl && (
                      <a href={p.proofUrl} target="_blank" rel="noreferrer" className="adm-btn adm-btn-ghost">
                        {t("viewProof")}
                      </a>
                    )}
                    {p.status === "pending" && (
                      <>
                        <button
                          type="button"
                          className="adm-btn adm-btn-confirm"
                          disabled={actingId === p.id}
                          onClick={() => act(p.id, "confirm")}
                        >
                          {actingId === p.id ? <Spinner animation="border" size="sm" /> : t("confirmPayment")}
                        </button>
                        <button
                          type="button"
                          className="adm-btn adm-btn-reject"
                          disabled={actingId === p.id}
                          onClick={() => act(p.id, "reject")}
                        >
                          {t("rejectPayment")}
                        </button>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </Container>
        <Footer logged={true} />
      </main>

      <style>{`
        /* Botones propios (no .btn) para escapar del override global de 60px/100%. */
        .adm-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 6px;
          height: auto !important; width: auto !important;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: .875rem; font-weight: 600;
          border: 1.5px solid transparent;
          cursor: pointer;
          text-decoration: none;
          transition: filter .15s ease, background .15s ease, transform .1s ease;
        }
        .adm-btn:disabled { opacity: .6; cursor: default; }
        .adm-btn:active:not(:disabled) { transform: scale(0.98); }
        .adm-btn-confirm { background: #00b894; color: #fff; }
        .adm-btn-confirm:hover:not(:disabled) { filter: brightness(1.05); }
        .adm-btn-reject { background: #fff; color: #d63031; border-color: #f2b8b5; }
        .adm-btn-reject:hover:not(:disabled) { background: #fff5f5; }
        .adm-btn-ghost { background: #f4f4f6; color: #444; }
        .adm-btn-ghost:hover { background: #e9e9ee; color: #222; }
      `}</style>
    </div>
  );
};

export default AdminPayments;
